import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); await refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep trying */ }
      if (tries++ < 10) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 text-center">
      {status === "checking" && <><Loader2 size={44} className="animate-spin text-[#F36353]" /><h1 className="mt-4 font-display text-2xl font-bold text-[#0F3D66]">Confirming your payment…</h1></>}
      {status === "paid" && (
        <div className="bp-fade-up">
          <CheckCircle2 size={54} className="mx-auto text-[#748C70]" />
          <h1 className="mt-4 font-display text-2xl font-bold text-[#0F3D66]">You're a Curator!</h1>
          <p className="mt-2 text-[#6B6763]">You can now create studies and invite friends.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Button onClick={() => nav("/host/new")} data-testid="create-first-study-btn" className="rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">Create your first study</Button>
            <Button onClick={() => nav("/plaza")} variant="outline" className="rounded-full border-[#E8E4DF]">Go to Plaza</Button>
          </div>
        </div>
      )}
      {["error", "timeout"].includes(status) && (
        <>
          <XCircle size={54} className="text-red-400" />
          <h1 className="mt-4 font-display text-2xl font-bold text-[#0F3D66]">Payment not confirmed</h1>
          <p className="mt-2 text-[#6B6763]">If you were charged, your Curator access will update shortly.</p>
          <Button onClick={() => nav("/upgrade")} className="mt-6 rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">Back to upgrade</Button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 text-center">
      <XCircle size={54} className="text-[#6B6763]" />
      <h1 className="mt-4 font-display text-2xl font-bold text-[#0F3D66]">Checkout canceled</h1>
      <p className="mt-2 text-[#6B6763]">No charge was made. You can upgrade any time.</p>
      <Button onClick={() => nav("/upgrade")} data-testid="cancel-back-btn" className="mt-6 rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">Back to upgrade</Button>
    </div>
  );
}
